import { createTheme, alpha, Shadows } from '@mui/material/styles';

/**
 * Civilhealth Premium Healthcare Theme
 * Light Theme with Royal Blue + Purple + Gold Accents
 * High Visibility Design
 */

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#3B82F6',
      light: '#60A5FA',
      dark: '#1D4ED8',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#8B5CF6',
      light: '#A78BFA',
      dark: '#6D28D9',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
    },
    warning: {
      main: '#F59E0B',
      light: '#FCD34D',
      dark: '#D97706',
    },
    error: {
      main: '#EF4444',
      light: '#F87171',
      dark: '#DC2626',
    },
    info: {
      main: '#3B82F6',
      light: '#60A5FA',
      dark: '#1D4ED8',
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1A2E',
      secondary: '#374151',
      disabled: '#9CA3AF',
    },
    divider: '#E5E7EB',
  },

  typography: {
    fontFamily: '"Inter", "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontSize: '3rem',
      fontWeight: 800,
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
      color: '#1A1A2E',
    },
    h2: {
      fontSize: '2.25rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      color: '#1A1A2E',
    },
    h3: {
      fontSize: '1.875rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.015em',
      color: '#1A1A2E',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
      color: '#1A1A2E',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#1A1A2E',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#1A1A2E',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#374151',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#6B7280',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
      color: '#374151',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.6,
      color: '#6B7280',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.9375rem',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: 1.4,
      color: '#6B7280',
    },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: '#F59E0B',
    },
  },

  shape: {
    borderRadius: 12,
  },

  shadows: [
    'none',
    '0 1px 2px rgba(0, 0, 0, 0.05)',
    '0 2px 4px rgba(0, 0, 0, 0.08)',
    '0 4px 8px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.12)',
    '0 16px 32px rgba(0, 0, 0, 0.14)',
    '0 24px 48px rgba(0, 0, 0, 0.16)',
    '0 0 30px rgba(59, 130, 246, 0.15)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
    '0 8px 16px rgba(0, 0, 0, 0.1)',
  ] as Shadows,

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: 'linear-gradient(135deg, #E8E8E8 0%, #F5F5F5 50%, #EBEBEB 100%)',
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
          color: '#1A1A2E',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            borderColor: '#3B82F6',
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundImage: 'none',
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
        },
        elevation0: {
          boxShadow: 'none',
          border: '1px solid #E5E7EB',
        },
        elevation1: {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 600,
          padding: '10px 24px',
          transition: 'all 0.3s ease',
        },
        contained: {
          background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
          boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
          color: '#FFFFFF',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
          boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
          boxShadow: '0 4px 14px rgba(139, 92, 246, 0.4)',
        },
        outlined: {
          borderColor: '#E5E7EB',
          color: '#374151',
          '&:hover': {
            borderColor: '#3B82F6',
            backgroundColor: 'rgba(59, 130, 246, 0.05)',
          },
        },
        text: {
          color: '#6B7280',
          '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.05)',
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 9999,
          fontWeight: 500,
          fontSize: '0.8125rem',
        },
        colorSuccess: {
          backgroundColor: '#ECFDF5',
          color: '#059669',
          border: '1px solid #A7F3D0',
        },
        colorWarning: {
          backgroundColor: '#FFFBEB',
          color: '#D97706',
          border: '1px solid #FCD34D',
        },
        colorError: {
          backgroundColor: '#FEF2F2',
          color: '#DC2626',
          border: '1px solid #FCA5A5',
        },
        colorInfo: {
          backgroundColor: '#EFF6FF',
          color: '#1D4ED8',
          border: '1px solid #93C5FD',
        },
        colorPrimary: {
          backgroundColor: '#EFF6FF',
          color: '#1D4ED8',
          border: '1px solid #93C5FD',
        },
        colorSecondary: {
          backgroundColor: '#F5F3FF',
          color: '#6D28D9',
          border: '1px solid #C4B5FD',
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#F9FAFB',
            color: '#1A1A2E',
            transition: 'all 0.3s ease',
            '& fieldset': {
              borderColor: '#E5E7EB',
            },
            '&:hover': {
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#3B82F6',
              },
            },
            '&.Mui-focused': {
              backgroundColor: '#FFFFFF',
              boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1)',
              '& fieldset': {
                borderColor: '#3B82F6',
              },
            },
          },
          '& .MuiInputLabel-root': {
            color: '#6B7280',
            '&.Mui-focused': {
              color: '#3B82F6',
            },
          },
          '& .MuiFormHelperText-root': {
            color: '#DC2626',
          },
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          // NO DEFAULT BACKGROUND - Icons should be visible with just color
          backgroundColor: 'transparent',
        },
      },
    },

    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          '&:hover': {
            backgroundColor: '#F3F4F6',
          },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.9375rem',
          minHeight: 48,
          color: '#6B7280',
          '&.Mui-selected': {
            fontWeight: 600,
            color: '#3B82F6',
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          background: 'linear-gradient(90deg, #3B82F6 0%, #8B5CF6 100%)',
          height: 3,
          borderRadius: 3,
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#1A1A2E',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          borderBottom: '1px solid #E5E7EB',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          borderRight: '1px solid #E5E7EB',
          boxShadow: '4px 0 16px rgba(0, 0, 0, 0.08)',
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#E5E7EB',
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 9999,
          height: 8,
          backgroundColor: '#E5E7EB',
        },
        bar: {
          borderRadius: 9999,
          background: 'linear-gradient(90deg, #3B82F6 0%, #8B5CF6 50%, #F59E0B 100%)',
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F9FAFB',
          '& .MuiTableCell-root': {
            color: '#374151',
            fontWeight: 600,
            borderBottom: '1px solid #E5E7EB',
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #E5E7EB',
          color: '#374151',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#F9FAFB',
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#3B82F6',
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#3B82F6',
          },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#9CA3AF',
          '&.Mui-checked': {
            color: '#3B82F6',
          },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#9CA3AF',
          '&.Mui-checked': {
            color: '#3B82F6',
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#F9FAFB',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E5E7EB',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#3B82F6',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#3B82F6',
          },
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#374151',
          borderRadius: 8,
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: '#F3F4F6',
          },
          '&.Mui-selected': {
            backgroundColor: '#EFF6FF',
            '&:hover': {
              backgroundColor: '#DBEAFE',
            },
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1A1A2E',
          color: '#FFFFFF',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: '#1A1A2E',
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: '#374151',
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: '1px solid #E5E7EB',
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        standardSuccess: {
          backgroundColor: '#ECFDF5',
          color: '#059669',
        },
        standardWarning: {
          backgroundColor: '#FFFBEB',
          color: '#D97706',
        },
        standardError: {
          backgroundColor: '#FEF2F2',
          color: '#DC2626',
        },
        standardInfo: {
          backgroundColor: '#EFF6FF',
          color: '#1D4ED8',
        },
      },
    },

    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#1A1A2E',
            color: '#FFFFFF',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
  },
});

export default theme;